/**
 * Plan-level questions and their options. Shared by the router (validates writes) and the
 * views, so a stored answer is always one the page can show. `cannibalization` feeds the
 * derived signal (`utils/demandFramingSignal.ts`); `seasonTrend` is how "Growth from comparable
 * seasons" applies its rate (`shared/insightOffers.ts`). One row per question and scope.
 */

import { SEASONALITY_PROFILE_FRAMING_OPTIONS } from "~/utils/demandSeasonalityProfile"

/** An option carrying its own value, stored as `<optionId>:<pct>`. 0–100 whole % unless set. */
export type FramingOptionValue = {
  control: "input" | "slider"
  label: string
  /** Applies to a bare option id. */
  default: number
  min?: number
  max?: number
  decimals?: 0 | 1
  unit?: "%" | "units"
  describe: (pct: number, context?: FramingDescribeContext) => string
  summary: (pct: number) => string
}

/** Under a node, each reading is the node's slice and carries its `nodeName`. */
export type FramingDescribeContext = {
  /** This season's variant count minus the baseline's. */
  variantDelta?: number
  season?: {
    /** Comparable season's growth over the one before; null when no rate is available. */
    pct: number | null
    /** Under a node: reconciled to the collection's total (`NodeSalesTrend.appliedPct`). */
    appliedPct?: number | null
    nodeName?: string
    /** Comparable season's sales; every option is a share of it. */
    year1: number
    unitsDelta: number
    comparableName: string
    priorName: string
    /** A planning hierarchy level is set, so the growth is split by category before variants. */
    byCategory?: boolean
  }
  stockout?: {
    nodeName?: string
    /** NOOS measures on the variants' own baseline sales, not on comparables. */
    ownSales?: boolean
    comparableSales: number
    lostUnits: number
  }
  /**
   * Where demand ends under Full cannibalization, formatted by the client (this file is shared
   * with the server). Absent when it can't be sized.
   */
  cannibalization?: {
    comparableName: string
    fullEndLabel: string
  }
  discounts?: {
    nodeName?: string
    /** NOOS measures on the variants' own baseline sales, not on comparables. */
    ownSales?: boolean
    comparableSales: number
    discountedSales: number
    /** ≤ 0. */
    impactUnits: number
  }
}

/** True minus sign, as every figure on the page prints one. */
const signedPctText = (pct: number): string => {
  if (pct > 0) return `+${pct}%`
  if (pct < 0) return `−${Math.abs(pct)}%`
  return "0%"
}
const signedUnitsText = (units: number): string =>
  `${units > 0 ? "+" : units < 0 ? "−" : ""}${Math.abs(Math.round(units)).toLocaleString("en-US")}`

/** Whose sales a stockout or discount figure is measured on. */
const measuredOn = (source?: { nodeName?: string; ownSales?: boolean }): string => {
  if (source?.ownSales)
    return source.nodeName ? `${source.nodeName}'s baseline variants` : "the baseline's variants"
  return source?.nodeName ? `${source.nodeName}'s comparable variants` : "the comparable variants"
}

const variantChangeWord = (delta: number | undefined): string => {
  if (delta == null || delta === 0) return "added or removed"
  return delta > 0 ? "added" : "removed"
}

type FramingOption = {
  id: string
  label: string
  nodeLabel?: string
  /** Refused on the whole collection. */
  nodeOnly?: boolean
  /** Wording while unselected or carrying no value. */
  description: string | ((context?: FramingDescribeContext) => string)
  value?: FramingOptionValue
}

export const isNodeOnlyOption = (question: FramingQuestionId, optionId: string): boolean =>
  FRAMING_QUESTIONS[question].options.find((o) => o.id === optionId)?.nodeOnly === true

export const framingOptionDescription = (
  option: { description: FramingOption["description"] },
  context?: FramingDescribeContext,
): string =>
  typeof option.description === "function" ? option.description(context) : option.description

export type FramingQuestion = {
  id: FramingQuestionId
  /** Phrased as a question, shown above the answer controls. */
  title: string
  options: readonly FramingOption[]
  /** Absent = unanswered until picked. */
  defaultOptionId?: string
}

export const FRAMING_QUESTION_IDS = [
  "cannibalization",
  "seasonTrend",
  "discounts",
  "stockout",
  "preorderDevelopment",
  "preorderKnownShare",
  "preorderSplit",
  "sizeSplitSource",
  "sizePreorderSplit",
  "phasingOut",
  "seasonalityProfile",
] as const
export type FramingQuestionId = (typeof FRAMING_QUESTION_IDS)[number]

/**
 * One node's baseline read (`getFramingBaseline`). Sides are named for the season window; in
 * the rolling window `new` holds the variants LEAVING the assortment and `carryOver` the ones
 * that keep selling.
 */
export type FramingBaseline = {
  variants: number
  newVariants: number
  carryOverVariants: number
  newUnits: number
  carryOverUnits: number
}

/** Weekly `DemandLifecycle` state counts; always sum to the variant count. */
export type LifecycleWeekCounts = {
  week: Date
  upcoming: number
  live: number
  ongoing: number
  phasingOut: number
  phasedOut: number
}

export const FRAMING_SEGMENTS = ["", "new", "carryOver"] as const
export type FramingSegment = (typeof FRAMING_SEGMENTS)[number]

/**
 * Both kinds share the labels though their sides mean different things (`FramingBaseline`).
 * The stored `segment` value never varies by kind: re-keying would orphan recorded answers.
 */
export const SEGMENT_LABEL: Record<FramingSegment, string> = {
  "": "All variants",
  new: "New variants",
  carryOver: "Carry-over",
}

/** `""` everywhere is the whole collection. */
export type FramingNode = { segment: FramingSegment; groupKey: string; groupValue: string }
export const WHOLE_COLLECTION_NODE: FramingNode = { segment: "", groupKey: "", groupValue: "" }

export type FramingAnswerRow = FramingNode & { question: FramingQuestionId; answer: string }

/**
 * Kept apart from the node rows: node readers match on segment/groupKey/groupValue alone, so a
 * variant row (blank on all three) would read as the collection's answer.
 */
export type FramingVariantAnswerRow = {
  question: FramingQuestionId
  variantId: string
  answer: string
}

/**
 * A sales slice: one value per configured sales planning level, `""` on an axis = every value of it.
 * Insight answers can be given per slice, always on every configured sales level at once.
 */
export type FramingSlice = { channel: string; warehouseId: string; marketId: string }
export const ALL_SLICE: FramingSlice = { channel: "", warehouseId: "", marketId: "" }
export const isAllSlice = (s: FramingSlice | undefined): boolean =>
  s == null || (s.channel === "" && s.warehouseId === "" && s.marketId === "")
export const sliceKey = (s: FramingSlice): string => `${s.channel}|${s.warehouseId}|${s.marketId}`

/** A slice answer for the collection (`variantId` `""`) or one variant; never on a node. */
export type FramingSliceAnswerRow = FramingSlice & {
  question: FramingQuestionId
  variantId: string
  answer: string
}

export type FramingAnswers = {
  answers: readonly FramingAnswerRow[]
  variantAnswers: readonly FramingVariantAnswerRow[]
  /** Kept apart for the same reason as `variantAnswers`; absent reads as none. */
  sliceAnswers?: readonly FramingSliceAnswerRow[]
}

export const NO_FRAMING_ANSWERS: FramingAnswers = {
  answers: [],
  variantAnswers: [],
  sliceAnswers: [],
}

/**
 * The insight questions, which describe how a variant sold, so a channel can answer differently.
 * Strategy questions (growth, cannibalization) shape the assortment and stay on assortment levels.
 */
export const SLICE_ANSWER_QUESTIONS = [
  "discounts",
  "stockout",
  "preorderDevelopment",
  "preorderKnownShare",
  "phasingOut",
] as const satisfies readonly FramingQuestionId[]

export type SliceAnswerQuestion = (typeof SLICE_ANSWER_QUESTIONS)[number]

export const canAnswerPerSlice = (question: FramingQuestionId): question is SliceAnswerQuestion =>
  (SLICE_ANSWER_QUESTIONS as readonly FramingQuestionId[]).includes(question)

/**
 * Not `seasonTrend`: a level rate split top-down, which the engine refuses per variant
 * (`LEVEL_SCOPED_INSIGHT_KEYS`). Not `preorderKnownShare`: it describes the season, not a variant.
 */
export const VARIANT_ANSWER_QUESTIONS: readonly FramingQuestionId[] = [
  "preorderDevelopment",
  "stockout",
  "discounts",
  "preorderSplit",
  "phasingOut",
]

// Per-variant choices that are not insights, so kept out of `VARIANT_ANSWER_QUESTIONS`.
const VARIANT_CHOICE_QUESTIONS: readonly FramingQuestionId[] = [
  "sizeSplitSource",
  "sizePreorderSplit",
  "seasonalityProfile",
]

export const canAnswerPerVariant = (question: FramingQuestionId): boolean =>
  VARIANT_ANSWER_QUESTIONS.includes(question) || VARIANT_CHOICE_QUESTIONS.includes(question)

type HierarchyNode = { groupKey: string; groupValue: string }

/**
 * A variant's `nodes` are in hierarchy order (widest first); it resolves as the engine does:
 * own row, deepest answering node, then the collection.
 */
export type AnswerScope =
  | { kind: "whole"; slice?: FramingSlice }
  | { kind: "node"; node: HierarchyNode }
  | { kind: "variant"; variantId: string; nodes: readonly HierarchyNode[]; slice?: FramingSlice }

export const WHOLE_SCOPE: AnswerScope = { kind: "whole" }

export const scopeNode = (scope: AnswerScope): FramingNode =>
  scope.kind === "node" ? { segment: "", ...scope.node } : WHOLE_COLLECTION_NODE

/** The `setDemandFramingAnswer` input minus the answer. */
export const scopeWriteKey = (
  scope: AnswerScope,
): FramingSlice & { segment: ""; groupKey: string; groupValue: string; variantId: string } => {
  if (scope.kind === "node")
    return {
      segment: "",
      groupKey: scope.node.groupKey,
      groupValue: scope.node.groupValue,
      variantId: "",
      ...ALL_SLICE,
    }
  return {
    segment: "",
    groupKey: "",
    groupValue: "",
    variantId: scope.kind === "variant" ? scope.variantId : "",
    ...(scope.slice ?? ALL_SLICE),
  }
}

const storedVariantAnswer = (
  rows: readonly FramingVariantAnswerRow[],
  question: FramingQuestionId,
  variantId: string,
): string | undefined =>
  rows.find((r) => r.question === question && r.variantId === variantId)?.answer

export const storedFramingAnswer = (
  rows: readonly FramingAnswerRow[],
  question: FramingQuestionId,
  node: FramingNode = WHOLE_COLLECTION_NODE,
): string | undefined =>
  rows.find(
    (r) =>
      r.question === question &&
      r.segment === node.segment &&
      r.groupKey === node.groupKey &&
      r.groupValue === node.groupValue,
  )?.answer

const capitalized = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * How a season growth figure reaches the variants, as `allocateSeasonTrendNodes` and
 * `spreadSeasonTrendUnits` split it. With no planning hierarchy level there are no categories: the
 * plan is one pool.
 */
const seasonSplitText = (byCategory: boolean | undefined, categories: string): string =>
  byCategory
    ? `split across categories ${categories}, then across variants by their unconfirmed demand`
    : "split across variants by their unconfirmed demand"

export const FRAMING_QUESTIONS: Record<FramingQuestionId, FramingQuestion> = {
  cannibalization: {
    id: "cannibalization",
    title: "How should the assortment changes be handled?",
    // Same as no stored answer: the engine's signal under `none` is 0%.
    defaultOptionId: "none",
    options: [
      {
        id: "none",
        label: "No cannibalization",
        description: "Assortment changes have their full effect on demand.",
      },
      {
        id: "partial",
        label: "Partial cannibalization",
        description: "Set how much of the assortment change is absorbed.",
        value: {
          control: "slider",
          label: "% absorbed",
          default: 50,
          // Shared with the server, so `formatName` (client storage) cannot run here. Plain word, on purpose.
          /* eslint-disable-next-line @hakio/no-untranslated-terminology */
          describe: (pct, ctx) =>
            `${pct}% of sales from the ${variantChangeWord(ctx?.variantDelta)} variants are absorbed by existing variants.`,
          summary: (pct) => `Partial cannibalization (${pct}%)`,
        },
      },
      {
        id: "full",
        label: "Full cannibalization",
        description: (ctx) => {
          const c = ctx?.cannibalization
          return c
            ? `Assortment changes do not affect demand, and it will be similar to ${c.comparableName} (${c.fullEndLabel}).`
            : "Assortment changes do not affect demand."
        },
      },
    ],
  },
  // Every option applies top-down on the comparable season's sales
  // (`utils/demandSeasonTrendMode.ts`). Default is no growth: the measured rate is history,
  // carried only where a planner says it repeats. Shared with the server, so `formatName`
  // (client storage) cannot run here.
  /* eslint-disable @hakio/no-untranslated-terminology */
  seasonTrend: {
    id: "seasonTrend",
    title: "How should the growth be applied?",
    defaultOptionId: "none",
    options: [
      {
        id: "pct",
        label: "Match % change",
        // Under a node: its rate scaled so categories sum to the collection's change.
        // `pctRaw` is unscaled.
        nodeLabel: "Match % change (scaled)",
        description: (ctx) => {
          const s = ctx?.season
          if (s?.nodeName) {
            return s.appliedPct != null
              ? `Grows ${s.nodeName} by ${signedPctText(Math.round(s.appliedPct * 10) / 10)} of its ${s.comparableName} sales: its own rate, scaled so the categories add up to the collection's change. Split across its variants by their unconfirmed demand.`
              : `Grows ${s.nodeName} by its own rate, scaled so the categories add up to the collection's change. Split across its variants by their unconfirmed demand.`
          }
          return s?.pct != null
            ? `Grows the plan by ${signedPctText(s.pct)} of ${s.comparableName}'s sales, the rate it grew over ${s.priorName}. ${capitalized(seasonSplitText(s.byCategory, "at each category's own rate"))}.`
            : `Grows the plan by the rate the comparable collection grew over the collection before it. ${capitalized(seasonSplitText(s?.byCategory, "at each category's own rate"))}.`
        },
      },
      {
        id: "pctRaw",
        label: "Match % change (raw)",
        nodeOnly: true,
        description: (ctx) => {
          const s = ctx?.season
          return s?.nodeName && s.pct != null
            ? `Grows ${s.nodeName} by its own ${signedPctText(s.pct)} of its ${s.comparableName} sales, as its chart shows, not scaled to the collection's change. Split across its variants by their unconfirmed demand.`
            : "Grows this node by its own measured rate, as its chart shows — not scaled to the collection's change."
        },
      },
      {
        id: "units",
        label: "Match unit change",
        description: (ctx) =>
          ctx?.season
            ? `Adds the ${signedUnitsText(ctx.season.unitsDelta)} units ${ctx.season.comparableName} grew by over ${ctx.season.priorName}, split the same way.`
            : "Adds the units the comparable collection grew by over the collection before it, split the same way.",
      },
      {
        id: "none",
        label: "No trend",
        description:
          "Every variant keeps its baseline. The growth between the comparable collections is not applied.",
      },
      {
        id: "custom",
        label: "Custom trend",
        description: "Grow the plan by a rate of your own on the comparable collection's sales.",
        value: {
          control: "input",
          label: "Growth to apply",
          default: 0,
          min: -100,
          max: 500,
          decimals: 1,
          describe: (pct, ctx) =>
            `Grows the plan by ${signedPctText(pct)} of ${ctx?.season?.comparableName ?? "the comparable collection"}'s sales, split the same way.`,
          summary: (pct) => `Custom trend (${signedPctText(pct)})`,
        },
      },
      {
        id: "customUnits",
        label: "Custom units",
        description: (ctx) =>
          `Add a number of units of your own, ${seasonSplitText(ctx?.season?.byCategory, "by their share of the comparable collection's sales")}.`,
        value: {
          control: "input",
          label: "Units to add",
          default: 0,
          min: -9_999_999,
          max: 9_999_999,
          decimals: 0,
          unit: "units",
          describe: (units, ctx) =>
            `Adds ${signedUnitsText(units)} units, ${seasonSplitText(ctx?.season?.byCategory, `by their share of ${ctx?.season?.comparableName ?? "the comparable collection"}'s sales`)}.`,
          summary: (units) => `Custom units (${signedUnitsText(units)})`,
        },
      },
    ],
  },
  // `maintain` is the default: the comparables' discounting is already in the baseline.
  discounts: {
    id: "discounts",
    title: "How should discounts be treated?",
    defaultOptionId: "maintain",
    options: [
      {
        id: "remove",
        label: "Remove discounts",
        description: (ctx) => {
          const d = ctx?.discounts
          const whose = measuredOn(d)
          const units =
            d != null && d.comparableSales > 0
              ? `${Math.abs(Math.round(d.impactUnits)).toLocaleString("en-US")} units `
              : "the units "
          return `Takes the ${units}${whose}' discounts added out of the plan. What is left is what would have sold at full price.`
        },
      },
      {
        id: "maintain",
        label: "Maintain similar discount levels",
        description: (ctx) => {
          const whose = measuredOn(ctx?.discounts)
          return `Plans on the same discounting ${whose} had, so the baseline does not change.`
        },
      },
    ],
  },
  /*
   * Labels and default are both kinds' (`adaptationLabels()`, `DEFAULT_FASHION_PREORDER_MODE`).
   */
  preorderDevelopment: {
    id: "preorderDevelopment",
    title: "How should the rest of the book be read?",
    defaultOptionId: "adaptChange",
    options: [
      {
        id: "closeBook",
        label: "Close the book",
        description: "Expect no further pre-orders on top of what is already confirmed.",
      },
      {
        id: "maintain",
        label: "Match baseline",
        description: "Assume the pre-orders land where the baseline did.",
      },
      {
        id: "adaptChange",
        label: "Maintain expectations",
        description: "Carry the book's movement against the baseline forward in units.",
      },
      {
        id: "adaptTrend",
        label: "Follow the trend",
        description: "Carry the book's movement against the baseline forward as a percentage.",
      },
    ],
  },
  /*
   * Planner override of the curve's confirmed-by-now share, the fashion pace denominator.
   * No `defaultOptionId`: unanswered means the curve's own share applies. Server-stored
   * because the engine applies it.
   */
  preorderKnownShare: {
    id: "preorderKnownShare",
    title: "How much of the collection is confirmed?",
    options: [
      {
        id: "share",
        label: "Set by you",
        description:
          "Read the pace against your own share of the collection rather than the curve's.",
        value: {
          control: "slider",
          label: "Share of the collection confirmed",
          default: 50,
          decimals: 1,
          describe: (pct) => `Treat ${pct}% of the collection's pre-orders as confirmed by now`,
          summary: (pct) => `${pct}% confirmed by now`,
        },
      },
    ],
  },
  // Where the season's expected pre-orders land across combinations (`preorderAwareLevels`). Plain
  // wording: this file is shared with the server, so the client's tenant naming is applied where it
  // renders (`PreorderSplitAnswer`).
  preorderSplit: {
    id: "preorderSplit",
    title: "Where should the forecasted pre-orders go?",
    defaultOptionId: "historic",
    options: [
      {
        id: "historic",
        label: "Towards the historic split",
        description: "Combinations below their historic share get more units.",
      },
      {
        id: "development",
        label: "On top of today's split",
        description: "Added on top of today's pre-orders, so combinations ahead stay ahead.",
      },
    ],
  },
  // The same choice across sizes, answered apart from `preorderSplit`: sizes split the expected units
  // within whatever warehouse split that answer produced (`sizeSplitUnits`). Client-only.
  sizePreorderSplit: {
    id: "sizePreorderSplit",
    title: "Where should the expected units go?",
    defaultOptionId: "historic",
    options: [
      {
        id: "historic",
        label: "Towards the estimated split",
        description: "Sizes below their estimated share get more units.",
      },
      {
        id: "development",
        label: "On top of today's split",
        description: "Added on top of today's pre-orders, so sizes ahead stay ahead.",
      },
    ],
  },
  // `ignore` is the default: lost demand is an estimate of sales that never happened.
  stockout: {
    id: "stockout",
    title: "How should stockouts be treated?",
    defaultOptionId: "ignore",
    options: [
      {
        id: "add",
        label: "Add the lost sales",
        description: (ctx) => {
          const st = ctx?.stockout
          const whose = measuredOn(st)
          const units =
            st != null && st.lostUnits > 0
              ? `${Math.round(st.lostUnits).toLocaleString("en-US")} units `
              : "the units "
          return `Adds the ${units}${whose} could not sell while out of stock. The plan is then what they would have sold.`
        },
      },
      {
        id: "ignore",
        label: "Ignore stockouts",
        description: (ctx) => {
          const whose = measuredOn(ctx?.stockout)
          return `Plans on what ${whose} actually sold, so the lost sales are not added.`
        },
      },
    ],
  },
  // `apply` is the default: a phase-out's sell-down is never in the baseline (NOOS_PLAN_PERIOD.md rule 5).
  phasingOut: {
    id: "phasingOut",
    title: "How should phasing out be treated?",
    defaultOptionId: "apply",
    options: [
      {
        id: "apply",
        label: "Plan the sell-down",
        description: "Plans only what the phasing-out variants can still sell before they end.",
      },
      {
        id: "ignore",
        label: "Ignore phasing out",
        description: "Plans the phasing-out variants on their full baseline, as if they stay.",
      },
    ],
  },
  sizeSplitSource: {
    id: "sizeSplitSource",
    title: "Which split should the sizes follow?",
    defaultOptionId: "recommended",
    options: [
      { id: "recommended", label: "AI", description: "Hakio's AI split." },
      {
        id: "similar",
        label: "Similar products",
        description: "Last year's sales of the most similar products.",
      },
      {
        id: "category",
        label: "Category",
        description: "Last year's sales across the whole category.",
      },
      { id: "manual", label: "Manual", description: "Your own split." },
    ],
  },
  seasonalityProfile: {
    id: "seasonalityProfile",
    title: "Which seasonality should the weeks follow?",
    options: SEASONALITY_PROFILE_FRAMING_OPTIONS,
  },
  /* eslint-enable @hakio/no-untranslated-terminology */
}

export type ParsedFramingAnswer = { optionId: string; customPct: number | null }

/**
 * Syntax only: `<optionId>` or `<optionId>:<number>` (signed, ≤ 1 decimal). Option and range
 * validity is {@link isFramingOption}'s job.
 */
export const parseFramingAnswer = (
  answer: string | null | undefined,
): ParsedFramingAnswer | null => {
  if (answer == null || answer === "") return null
  const at = answer.indexOf(":")
  if (at === -1) return { optionId: answer, customPct: null }
  const optionId = answer.slice(0, at)
  const raw = answer.slice(at + 1)
  // Seven digits so a units figure fits; `isFramingOption` still bounds the range.
  if (optionId === "" || !/^-?\d{1,7}(\.\d)?$/.test(raw)) return null
  return { optionId, customPct: Number(raw) }
}

const WHOLE_PERCENT: Required<Pick<FramingOptionValue, "min" | "max" | "decimals">> = {
  min: 0,
  max: 100,
  decimals: 0,
}

export const clampFramingValue = (pct: number, value?: FramingOptionValue): number => {
  const { min, max, decimals } = { ...WHOLE_PERCENT, ...value }
  const scale = 10 ** decimals
  return Math.min(max, Math.max(min, Math.round(pct * scale) / scale))
}

const inRange = (pct: number, value: FramingOptionValue): boolean => {
  const { min, max, decimals } = { ...WHOLE_PERCENT, ...value }
  return pct >= min && pct <= max && (decimals === 1 || Number.isInteger(pct))
}

/** `value` is required so a caller can't drop the option's spec and fall back to whole percents. */
export const valuedAnswer = (
  optionId: string,
  pct: number,
  value: FramingOptionValue | undefined,
): string => `${optionId}:${clampFramingValue(pct, value)}`

const optionOf = (question: FramingQuestionId, optionId: string): FramingOption | undefined =>
  FRAMING_QUESTIONS[question].options.find((o) => o.id === optionId)

/** Stored value, else the option's default (so a bare valued id still simulates); null if none. */
export const framingAnswerValue = (
  question: FramingQuestionId,
  answer: string | null | undefined,
): number | null => {
  const parsed = parseFramingAnswer(answer)
  const option = parsed ? optionOf(question, parsed.optionId) : undefined
  if (!option?.value) return null
  return parsed?.customPct ?? option.value.default
}

export const effectiveFramingAnswer = (
  question: FramingQuestionId,
  stored: string | null | undefined,
): string | undefined => stored ?? FRAMING_QUESTIONS[question].defaultOptionId

export type FramingResolution = {
  answer: string | undefined
  /** No answer at this scope; a wider one applies. */
  inherited: boolean
  /**
   * Nothing stored here or wider: the engine sizes with the default, but nobody chose it.
   * Pickers must not show it as a planner's decision.
   */
  isDefault: boolean
}

/** Own answer, else the first parent that answers, else the question's default. */
export const effectiveNodeAnswer = (
  question: FramingQuestionId,
  own: string | null | undefined,
  ...parents: (string | null | undefined)[]
): FramingResolution => {
  if (own != null) return { answer: own, inherited: false, isDefault: false }
  const parent = parents.find((p) => p != null)
  return {
    answer: effectiveFramingAnswer(question, parent),
    inherited: true,
    isDefault: parent == null,
  }
}

/**
 * Nearest first: the node in its segment, the segment alone, the node across all variants,
 * the whole collection, then the question's default.
 */
export const effectiveAnswerAt = (
  rows: readonly FramingAnswerRow[],
  question: FramingQuestionId,
  node: FramingNode,
): FramingResolution => {
  const at = (n: FramingNode) => storedFramingAnswer(rows, question, n)
  const parents = resolutionParents(node)
  const isWhole = node.segment === "" && node.groupKey === ""
  const own = at(node)
  if (isWhole)
    return {
      answer: effectiveFramingAnswer(question, own),
      inherited: false,
      isDefault: own == null,
    }
  return effectiveNodeAnswer(question, own, ...parents.map(at))
}

/**
 * Every (question, variant) a variant answers on its own, for the whole variant or any sales
 * slice of it, once per pair: what "own answer" counts and filters on.
 */
export const ownVariantAnswers = (framing: FramingAnswers): FramingVariantAnswerRow[] => {
  const seen = new Map<string, FramingVariantAnswerRow>()
  for (const a of framing.variantAnswers) seen.set(`${a.question}|${a.variantId}`, a)
  for (const a of framing.sliceAnswers ?? []) {
    if (a.variantId === "") continue
    const key = `${a.question}|${a.variantId}`
    if (!seen.has(key))
      seen.set(key, { question: a.question, variantId: a.variantId, answer: a.answer })
  }
  return [...seen.values()]
}

export const storedSliceAnswer = (
  rows: readonly FramingSliceAnswerRow[] | undefined,
  question: FramingQuestionId,
  variantId: string,
  slice: FramingSlice,
): string | undefined =>
  rows?.find(
    (r) =>
      r.question === question &&
      r.variantId === variantId &&
      r.channel === slice.channel &&
      r.warehouseId === slice.warehouseId &&
      r.marketId === slice.marketId,
  )?.answer

/**
 * A variant reads its own slice row, its own row, the collection's slice row, its nodes deepest
 * first (the engine's `resolveNodeMode` walk), then the collection, then the default; the engine's
 * `resolveVariantMode` walks the same order. A collection slice reads its own row, then the
 * collection. Other scopes resolve as {@link effectiveAnswerAt}.
 */
export const effectiveAnswerFor = (
  framing: FramingAnswers,
  question: FramingQuestionId,
  scope: AnswerScope,
): FramingResolution => {
  const slice = scope.kind !== "node" && !isAllSlice(scope.slice) ? scope.slice! : null
  const wholeSlice = slice && storedSliceAnswer(framing.sliceAnswers, question, "", slice)
  if (scope.kind === "whole" && slice)
    return effectiveNodeAnswer(question, wholeSlice, storedFramingAnswer(framing.answers, question))
  if (scope.kind !== "variant")
    return effectiveAnswerAt(framing.answers, question, scopeNode(scope))
  const ownSlice =
    slice && storedSliceAnswer(framing.sliceAnswers, question, scope.variantId, slice)
  const own = storedVariantAnswer(framing.variantAnswers, question, scope.variantId)
  const at = (node: HierarchyNode) =>
    node.groupValue === ""
      ? undefined
      : storedFramingAnswer(framing.answers, question, { segment: "", ...node })
  const parents = [...scope.nodes].reverse().map(at)
  if (slice)
    return effectiveNodeAnswer(
      question,
      ownSlice,
      own,
      wholeSlice,
      ...parents,
      storedFramingAnswer(framing.answers, question),
    )
  if (own != null) return { answer: own, inherited: false, isDefault: false }
  return effectiveNodeAnswer(
    question,
    undefined,
    ...parents,
    storedFramingAnswer(framing.answers, question),
  )
}

const resolutionParents = (node: FramingNode): FramingNode[] => {
  const whole = WHOLE_COLLECTION_NODE
  const parents: FramingNode[] = []
  if (node.segment !== "") parents.push({ ...whole, segment: node.segment })
  if (node.groupKey !== "")
    parents.push({ ...whole, groupKey: node.groupKey, groupValue: node.groupValue })
  parents.push(whole)
  return parents
}

/**
 * The stored row a node follows, along the {@link effectiveAnswerAt} chain. `undefined` when
 * only the question's default applies.
 */
export const storedAnswerScopeAt = (
  rows: readonly FramingAnswerRow[],
  question: FramingQuestionId,
  node: FramingNode,
): FramingAnswerRow | undefined => {
  const find = (n: FramingNode) =>
    rows.find(
      (r) =>
        r.question === question &&
        r.segment === n.segment &&
        r.groupKey === n.groupKey &&
        r.groupValue === n.groupValue,
    )
  const own = find(node)
  if (own) return own
  for (const parent of resolutionParents(node)) {
    const hit = find(parent)
    if (hit) return hit
  }
  return undefined
}

export const isFramingOption = (question: FramingQuestionId, answer: string): boolean => {
  const parsed = parseFramingAnswer(answer)
  if (!parsed) return false
  const option = optionOf(question, parsed.optionId)
  if (!option) return false
  if (parsed.customPct == null) return true
  return option.value != null && inRange(parsed.customPct, option.value)
}

export const framingOptionLabel = (
  question: FramingQuestionId,
  answer: string | null | undefined,
  /** `"node"` where the answer was given on a hierarchy node, for options that read differently there. */
  scope?: "node",
): string | null => {
  const parsed = parseFramingAnswer(answer)
  if (!parsed) return null
  const option = optionOf(question, parsed.optionId)
  if (!option) return null
  const pct = framingAnswerValue(question, answer)
  if (option.value && pct != null) return option.value.summary(pct)
  return (scope === "node" ? option.nodeLabel : undefined) ?? option.label
}

/**
 * The fashion Insights card's questions, in display order. The planning process counts them to
 * tell an adjusted plan from an opened one. `preorderKnownShare` is excluded: it overrides the
 * curve behind `preorderDevelopment` rather than asking its own question.
 */
export const FASHION_ADJUSTMENT_QUESTIONS: readonly FramingQuestionId[] = [
  "preorderDevelopment",
  "stockout",
  "discounts",
]

/** Counted with the strategy step. Cannibalization is not listed: it needs no season lineage. */
export const FASHION_STRATEGY_QUESTIONS: readonly FramingQuestionId[] = ["seasonTrend"]

/** Whole-collection answers only: a node answer decides one slice, not the plan. */
export const answeredQuestionCount = (
  rows: readonly FramingAnswerRow[],
  questions: readonly FramingQuestionId[] = FASHION_ADJUSTMENT_QUESTIONS,
): number => questions.filter((question) => storedFramingAnswer(rows, question) != null).length

/**
 * The questions there is something to answer for: one no variant offers is greyed out in Insights
 * (e.g. no stockouts in the baseline), so it can't hold the step open.
 */
export const answerableQuestions = (
  questions: readonly FramingQuestionId[],
  offered: ReadonlySet<string>,
): FramingQuestionId[] => questions.filter((question) => offered.has(question))

export type SalesSplit = { newUnits: number; carryOverUnits: number }
export type SplitCounts = { newVariants: number; carryOverVariants: number }

export const sliceBaseline = (b: FramingBaseline, segment: FramingSegment): FramingBaseline => {
  if (segment === "") return b
  const isNew = segment === "new"
  return {
    variants: isNew ? b.newVariants : b.carryOverVariants,
    newVariants: isNew ? b.newVariants : 0,
    carryOverVariants: isNew ? 0 : b.carryOverVariants,
    newUnits: isNew ? b.newUnits : 0,
    carryOverUnits: isNew ? 0 : b.carryOverUnits,
  }
}

export type CannibalizationSimulation = {
  units: number
  perVariant: number
}

/**
 * This season's sales from the baseline's units per variant: `none` keeps the rate (total
 * moves with the count), `full` holds the total, `partial` sits between by its absorbed share.
 * `null` until answered, or with no baseline rate or no variants this season.
 */
export const simulateCannibalization = (
  baseline: { variants: number; units: number },
  seasonVariants: number,
  answer: string | null | undefined,
): CannibalizationSimulation | null => {
  const option = parseFramingAnswer(answer)?.optionId
  if (option == null || baseline.variants <= 0 || seasonVariants <= 0) return null
  const rate = baseline.units / baseline.variants
  const addedOnTop = rate * seasonVariants
  const held = baseline.units
  let units: number
  switch (option) {
    case "none":
      units = addedOnTop
      break
    case "full":
      units = held
      break
    case "partial": {
      const cannibalized = (framingAnswerValue("cannibalization", answer) ?? 50) / 100
      units = held + (addedOnTop - held) * (1 - cannibalized)
      break
    }
    default:
      return null
  }
  return { units, perVariant: units / seasonVariants }
}

/**
 * The `simulateCannibalization` total split by side, so the split never moves the total.
 * `newOnly` holds carry-over at its baseline units and gives the new side the change (ignored
 * with no new variants). Otherwise split by each side's no-cannibalization weight; a side with
 * no baseline variants borrows the overall rate.
 */
export const simulateSeasonSplit = (
  baseline: FramingBaseline,
  season: SplitCounts & { variants: number },
  answer: string | null | undefined,
  newOnly: boolean,
): SalesSplit | null => {
  const lyUnits = baseline.newUnits + baseline.carryOverUnits
  const total = simulateCannibalization(
    { variants: baseline.variants, units: lyUnits },
    season.variants,
    answer,
  )
  if (!total) return null
  if (newOnly && season.newVariants > 0) {
    const carryOverUnits = season.carryOverVariants > 0 ? baseline.carryOverUnits : 0
    return { newUnits: Math.max(0, total.units - carryOverUnits), carryOverUnits }
  }
  const overallRate = lyUnits / baseline.variants
  const rate = (units: number, variants: number) => (variants > 0 ? units / variants : overallRate)
  const newWeight = rate(baseline.newUnits, baseline.newVariants) * season.newVariants
  const carryOverWeight =
    rate(baseline.carryOverUnits, baseline.carryOverVariants) * season.carryOverVariants
  const weight = newWeight + carryOverWeight
  if (weight <= 0) return null
  return {
    newUnits: (total.units * newWeight) / weight,
    carryOverUnits: (total.units * carryOverWeight) / weight,
  }
}
